/* eslint-disable @typescript-eslint/no-explicit-any */
import { captureException } from "@sentry/react";
import { useEffect, useMemo } from "react";
import { BehaviorSubject, distinctUntilChanged, Observable, tap } from "rxjs";
import { useSyncExternalStore } from "use-sync-external-store/shim";

type useObservableType = <T, InitT = T | undefined>(
  obs: Observable<T> | null,
  initial?: InitT,
  params?: any[] | null,
) => T | InitT;

export const useObservable: useObservableType = (obs$, initial = undefined, params: any[] | null = null) => {
  const data$ = useMemo(() => new BehaviorSubject<any>(initial), []);
  useEffect(() => () => data$.complete(), []);
  const sub = useMemo(
    () => (onStorageChange: () => void) => {
      const subscription = obs$
        ?.pipe(
          distinctUntilChanged(),
          tap((r) => data$.next(r)),
        )
        ?.subscribe({
          next: onStorageChange,
          error: (e) => {
            captureException(e);
          },
        });
      return () => subscription?.unsubscribe();
    },
    [...(params ?? [obs$]), data$],
  );
  const getCur = useMemo(() => () => data$?.getValue() ?? null, [data$]);
  return useSyncExternalStore(sub, getCur, getCur);
};
